import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";
import { partition as _partition } from 'lodash';

import { MarkerType, svgToDataUrl } from "../../../../../store";

import { markers, StateType } from "..";

import { Point } from './Point';
import { createElement } from "react";

class Json {
  #value: object | undefined = undefined;

  constructor(data: string) {
    try {
      this.#value = JSON.parse(data);
    } catch (_) {}
  }

  valueOf() {
    return this.#value;
  }
}

const serializableConstructors: Record<string, new (data: string) => any> = {
  Point,
  Number,
  String,
  Json,
};

export function serializeKeyValue(property: string, name: string, value: any): [string, string] {
  // We need to store the properties without uppercase letters, as that's technically not allowed by
  // the HTML spect and thus React warns us about it. See https://react.dev/warnings/unknown-prop
  const key = `${property}-${name}`;

  if (typeof value === 'string') {
    return [key, `String:${value}`];
  } else if (typeof value === 'number') {
    return [key, `Number:${value.toFixed(3)}`];
  } else if ('serialize' in value) {
    return [key, `${value.constructor.name}:${value.serialize()}`];
  } else if (value instanceof Object && value !== null) {
    return [key, `Json:${JSON.stringify(value)}`];
  } else {
    throw new Error(`Unknown type ${typeof value} in serializeKeyValue: ${property}, ${name}, ${value}`);
  }
}

export function deserializeKeyValue(key: string, value: string): [string, string | [string, any]] | null {
  const [property, ...nameParts] = key.split('-');

  if (nameParts.length === 0) return [property, value];

  const name = nameParts.length > 0 ? nameParts.join('-') : property;

  const [constructorName, ...rest] = value.split(':');
  if (rest.length === 0) return null;
  const data = rest.join(':');

  if (!(constructorName in serializableConstructors)) throw new Error(`Unknown constructor ${constructorName}`);

  const boxed = new serializableConstructors[constructorName](data);
  const unboxed = 'valueOf' in boxed ? boxed.valueOf() : boxed;

  return [ property, [name, unboxed] ];
}

const serializationRootEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
serializationRootEl.setAttribute('viewBox', "-1 -1 2 2");
serializationRootEl.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
const serializationRoot = createRoot(serializationRootEl);

// Get marker as base64-encoded SVG data URL
export function serializeMarker(state: StateType, id: string): string {
  serializationRootEl.setAttribute('id', `marker-${id}`);

  const s = Object.entries(state.active)
        .map(([step, partName]) => {
          const part = (markers as any)[step][partName];
          const SerializeContent = part.SerializeContent.bind(part);
          return createElement(SerializeContent, { id: id });
        });
  flushSync(() => {
    serializationRoot.render(s);
  });

  return svgToDataUrl(serializationRootEl.outerHTML);
}

export function deserializeMarker(storeMarker: MarkerType | undefined) {
  if (!storeMarker?.state) return null;

  try {
    // Parse the JSON state
    const fullState = JSON.parse(storeMarker.state);
    const activeState = fullState.active as StateType['active'];

    // Restore each marker part's state
    Object.entries(activeState).forEach(([step, partName], index) => {
      if (index >= fullState.parts.length) return;

      const part = (markers as any)[step]?.[partName];
      if (!part) return;

      try {
        // Parse the serialized state for this part
        const partSerializedState = JSON.parse(fullState.parts[index]);

        // Extract and deserialize attributes
        const attributes = Object.entries(partSerializedState)
        .map(([attr, value]) => {
          return deserializeKeyValue(attr, value as string);
        })
        .filter((deserialized) => deserialized !== null);

        // Separate flat and nested attributes
        const [_flatAttributes, nestedAttributes] = _partition(
          attributes,
          ([, value]) => typeof value === 'string'
        );

        // Restore the part's state
        nestedAttributes.forEach(([entry, [key, value]]) => {
          if (entry === 'state' && key in part.controlPoints) {
            // Restore control point positions
            part.controlPoints[key] = value;
          } else if (entry === 'reactiveState') {
            // Restore reactive state (colors, styles, etc.)
            if (key in part.reactiveState.internal) {
              part.reactiveState.internal[key] = value;
            }
          }
        });

      } catch (e) {
        console.warn(`Failed to deserialize part ${step}.${partName}:`, e);
      }
    });

    return activeState;

  } catch (e) {
    console.error('Failed to deserialize marker:', e);
    return null;
  }
}
